import React, { useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Trans } from 'react-i18next';

interface FAQItem {
  id: string;
  question: string;
  answer: ReactNode;
  category: string;
}

const FAQPage: React.FC = () => {
  const { t } = useTranslation();
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const faqItems: FAQItem[] = [
    // General Questions
    {
      id: 'what-is-tvia',
      question: t('faq.general.whatIsTvia.question', 'What is tvia.ai?'),
      answer: (
        <Trans i18nKey="faq.general.whatIsTvia.answer">
          <span className="font-brand">tvia.ai</span> is an AI-powered legal platform that helps individuals and businesses file lawsuits, join class actions, and access legal document templates. We make legal processes more accessible and affordable through technology.
        </Trans>
      ),
      category: 'general'
    },
    {
      id: 'how-it-works',
      question: t('faq.general.howItWorks.question', 'How does the platform work?'),
      answer: t('faq.general.howItWorks.answer', 'Our platform uses advanced AI to guide you through legal processes step-by-step. You provide information about your case, and our system generates the necessary legal documents and guides you through filing procedures.'),
      category: 'general'
    },
    {
      id: 'is-it-legal',
      question: t('faq.general.isItLegal.question', 'Is this service legal?'),
      answer: t('faq.general.isItLegal.answer', 'Yes, our service is completely legal. We provide tools and guidance to help you navigate legal processes, but we do not provide legal advice. We recommend consulting with a qualified attorney for complex legal matters.'),
      category: 'general'
    },

    // Account & Registration
    {
      id: 'create-account',
      question: t('faq.account.createAccount.question', 'How do I create an account?'),
      answer: t('faq.account.createAccount.answer', 'Creating an account is simple. Click the "Sign Up" button, provide your email address, create a password, and verify your email. You can also sign up using your Google or Facebook account for convenience.'),
      category: 'account'
    },
    {
      id: 'account-security',
      question: t('faq.account.security.question', 'How secure is my account?'),
      answer: t('faq.account.security.answer', 'We take security seriously. All data is encrypted, we use secure authentication methods, and we never share your personal information with third parties without your consent. We also offer two-factor authentication for additional security.'),
      category: 'account'
    },
    {
      id: 'reset-password',
      question: t('faq.account.resetPassword.question', 'How do I reset my password?'),
      answer: t('faq.account.resetPassword.answer', 'If you\'ve forgotten your password, click "Forgot Password" on the login page. Enter your email address, and we\'ll send you a secure link to reset your password.'),
      category: 'account'
    },

    // Lawsuits & Legal Actions
    {
      id: 'file-lawsuit',
      question: t('faq.lawsuits.fileLawsuit.question', 'How do I file a lawsuit?'),
      answer: t('faq.lawsuits.fileLawsuit.answer', 'To file a lawsuit, start by clicking "File a Lawsuit" in your dashboard. Our AI will guide you through the process, asking questions about your case and generating the necessary legal documents. You can then review, edit, and submit the documents.'),
      category: 'lawsuits'
    },
    {
      id: 'lawsuit-cost',
      question: t('faq.lawsuits.cost.question', 'How much does it cost to file a lawsuit?'),
      answer: t('faq.lawsuits.cost.answer', 'The cost varies depending on your subscription plan and the complexity of your case. Basic filing assistance is included in our free plan, while advanced features require a paid subscription. Court filing fees are separate and vary by jurisdiction.'),
      category: 'lawsuits'
    },
    {
      id: 'join-class-action',
      question: t('faq.lawsuits.joinClassAction.question', 'How do I join a class action lawsuit?'),
      answer: t('faq.lawsuits.joinClassAction.answer', 'Browse our class action directory to find lawsuits that may apply to you. Click "Join Lawsuit" and provide the required information. Our system will verify your eligibility and add you to the class action.'),
      category: 'lawsuits'
    },
    {
      id: 'lawsuit-status',
      question: t('faq.lawsuits.status.question', 'How can I check the status of my lawsuit?'),
      answer: t('faq.lawsuits.status.answer', 'You can track your lawsuit status in your dashboard. We provide real-time updates on filing status, court responses, and any required actions. You\'ll also receive email notifications for important updates.'),
      category: 'lawsuits'
    },

    // Payments & Billing
    {
      id: 'payment-methods',
      question: t('faq.payments.methods.question', 'What payment methods do you accept?'),
      answer: t('faq.payments.methods.answer', 'We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and bank transfers for annual plans. All payments are processed securely through our payment partners.'),
      category: 'payments'
    },
    {
      id: 'subscription-cancel',
      question: t('faq.payments.cancel.question', 'How do I cancel my subscription?'),
      answer: t('faq.payments.cancel.answer', 'You can cancel your subscription at any time from your account settings. Go to "Billing" in your dashboard and click "Cancel Subscription." You\'ll continue to have access until the end of your current billing period.'),
      category: 'payments'
    },
    {
      id: 'refund-policy',
      question: t('faq.payments.refund.question', 'What is your refund policy?'),
      answer: t('faq.payments.refund.answer', 'We offer a 30-day money-back guarantee for all paid plans. If you\'re not satisfied with our service, contact our support team within 30 days of your purchase for a full refund.'),
      category: 'payments'
    },

    // Legal Documents
    {
      id: 'document-templates',
      question: t('faq.documents.templates.question', 'What legal document templates do you offer?'),
      answer: t('faq.documents.templates.answer', 'We offer a wide range of legal document templates including contracts, agreements, letters, forms, and more. Our library covers business, employment, real estate, family law, and other legal areas.'),
      category: 'documents'
    },
    {
      id: 'document-customization',
      question: t('faq.documents.customization.question', 'Can I customize the legal documents?'),
      answer: t('faq.documents.customization.answer', 'Yes, all our document templates are fully customizable. You can edit text, add or remove sections, and modify terms to suit your specific needs. Our AI will guide you through the customization process.'),
      category: 'documents'
    },
    {
      id: 'document-validity',
      question: t('faq.documents.validity.question', 'Are the legal documents legally valid?'),
      answer: t('faq.documents.validity.answer', 'Our documents are based on standard legal templates and are designed to be legally sound. However, laws vary by jurisdiction, so we recommend having a qualified attorney review important documents before use.'),
      category: 'documents'
    },

    // Privacy & Security
    {
      id: 'data-privacy',
      question: t('faq.privacy.data.question', 'How do you protect my personal information?'),
      answer: t('faq.privacy.data.answer', 'We use industry-standard encryption to protect your data, both in transit and at rest. We never sell your personal information and only share it when required by law or with your explicit consent.'),
      category: 'privacy'
    },
    {
      id: 'gdpr-compliance',
      question: t('faq.privacy.gdpr.question', 'Are you GDPR compliant?'),
      answer: t('faq.privacy.gdpr.answer', 'Yes, we are fully GDPR compliant. We respect your right to access, modify, and delete your personal data. You can manage your data preferences in your account settings.'),
      category: 'privacy'
    },
    {
      id: 'data-retention',
      question: t('faq.privacy.retention.question', 'How long do you keep my data?'),
      answer: t('faq.privacy.retention.answer', 'We retain your data for as long as your account is active and for a reasonable period afterward to comply with legal obligations. You can request data deletion at any time through your account settings.'),
      category: 'privacy'
    }
  ];

  const categories = [
    { id: 'all', name: t('faq.categories.all', 'All Questions') },
    { id: 'general', name: t('faq.categories.general', 'General') },
    { id: 'account', name: t('faq.categories.account', 'Account & Registration') },
    { id: 'lawsuits', name: t('faq.categories.lawsuits', 'Lawsuits & Legal Actions') },
    { id: 'payments', name: t('faq.categories.payments', 'Payments & Billing') },
    { id: 'documents', name: t('faq.categories.documents', 'Legal Documents') },
    { id: 'privacy', name: t('faq.categories.privacy', 'Privacy & Security') }
  ];

  const filteredItems = faqItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.answer && item.answer.toString().toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  const toggleItem = (itemId: string) => {
    setOpenItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('faq.title', 'Frequently Asked Questions')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('faq.subtitle', 'Find answers to the most common questions about our platform and services.')}
          </p>
        </div>

        {/* Search */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('faq.searchPlaceholder', 'Search questions...')}
              className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
            <svg
              className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                selectedCategory === category.id
                  ? 'bg-cyan-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Results Count */}
        <div className="text-center mb-8">
          <p className="text-gray-600">
            {t('faq.results', '{{count}} questions found', { count: filteredItems.length })}
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                {t('faq.noResults', 'No questions found')}
              </h3>
              <p className="text-gray-600">
                {t('faq.noResultsDescription', 'Try adjusting your search or category filter.')}
              </p>
            </div>
          ) : (
            filteredItems.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  onClick={() => toggleItem(item.id)}
                  className="w-full px-6 py-4 text-left focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-inset"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {item.question}
                    </h3>
                    <svg
                      className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${
                        openItems.includes(item.id) ? 'rotate-180' : ''
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </button>
                {openItems.includes(item.id) && (
                  <div className="px-6 pb-4">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-700 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Contact Support */}
        <div className="mt-16 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-lg p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">
            {t('faq.needMoreHelp', 'Still have questions?')}
          </h2>
          <p className="text-lg mb-6 opacity-90">
            {t('faq.contactSupport', 'Can\'t find the answer you\'re looking for? Our support team is here to help.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-cyan-600 px-6 py-3 rounded-md font-medium hover:bg-gray-100 transition-colors duration-200"
            >
              {t('faq.contactUs', 'Contact Us')}
            </Link>
            <Link
              to="/help"
              className="border-2 border-white text-white px-6 py-3 rounded-md font-medium hover:bg-white hover:text-cyan-600 transition-colors duration-200"
            >
              {t('faq.helpCenter', 'Help Center')}
            </Link>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            {t('faq.quickLinks', 'Quick Links')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/help"
              className="bg-white rounded-lg shadow-sm p-6 text-center hover:shadow-md transition-shadow duration-200"
            >
              <div className="text-3xl mb-4">📚</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('faq.helpCenter', 'Help Center')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('faq.helpCenterDescription', 'Comprehensive help documentation')}
              </p>
            </Link>
            <Link
              to="/terms"
              className="bg-white rounded-lg shadow-sm p-6 text-center hover:shadow-md transition-shadow duration-200"
            >
              <div className="text-3xl mb-4">⚖️</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('faq.legalDisclaimer', 'Terms of Service')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('faq.legalDisclaimerDescription', 'Important legal information')}
              </p>
            </Link>
            <Link
              to="/contact"
              className="bg-white rounded-lg shadow-sm p-6 text-center hover:shadow-md transition-shadow duration-200"
            >
              <div className="text-3xl mb-4">📞</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('faq.contact', 'Contact Support')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('faq.contactDescription', 'Get in touch with our team')}
              </p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;